import React, { useEffect, useState } from 'react';
import api from '../api';
import Modal from '../components/Modal';
import { useAuth } from '../context/AuthContext';

const ROLLEN = ['admin', 'schwimmadmin', 'trainer', 'readonly'];
const ROLLEN_LABELS = { admin: 'Administrator', schwimmadmin: 'Schwimmadmin', trainer: 'Trainer', readonly: 'Nur lesen' };

function fmtDate(d) { return d ? new Date(d).toLocaleDateString('de-DE') : '–'; }

const INIT_FORM = { vorname: '', nachname: '', email: '', rolle: 'schwimmadmin', passwort: '' };

export default function Benutzer() {
  const { user: aktuellerBenutzer } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState(INIT_FORM);
  const [createError, setCreateError] = useState('');
  const [creating, setCreating] = useState(false);

  const [selected, setSelected] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editError, setEditError] = useState('');
  const [saving, setSaving] = useState(false);
  const [deactivateConfirm, setDeactivateConfirm] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/admin/benutzer');
      setRows(res.data);
    } catch {
      setError('Benutzer konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!createForm.vorname || !createForm.nachname || !createForm.email || !createForm.passwort) {
      setCreateError('Alle Felder sind Pflicht');
      return;
    }
    setCreating(true);
    setCreateError('');
    try {
      await api.post('/api/admin/benutzer', createForm);
      setShowCreate(false);
      setCreateForm(INIT_FORM);
      await load();
    } catch (err) {
      setCreateError(err.response?.data?.error || 'Fehler beim Anlegen');
    } finally {
      setCreating(false);
    }
  };

  const openEdit = (row) => {
    setSelected(row);
    setEditForm({ vorname: row.vorname, nachname: row.nachname, rolle: row.rolle, passwort: '' });
    setEditError('');
    setDeactivateConfirm(false);
  };

  const handleSave = async () => {
    setSaving(true);
    setEditError('');
    try {
      const payload = { vorname: editForm.vorname, nachname: editForm.nachname, rolle: editForm.rolle };
      if (editForm.passwort) payload.passwort = editForm.passwort;
      await api.put(`/api/admin/benutzer/${selected.id}`, payload);
      await load();
      setSelected(null);
    } catch (err) {
      setEditError(err.response?.data?.error || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async () => {
    if (!deactivateConfirm) { setDeactivateConfirm(true); return; }
    setSaving(true);
    try {
      await api.delete(`/api/admin/benutzer/${selected.id}`);
      await load();
      setSelected(null);
    } catch (err) {
      setEditError(err.response?.data?.error || 'Fehler');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="page-header">
        <h2 className="page-title">Benutzerverwaltung</h2>
        <button className="btn btn-primary btn-sm" onClick={() => { setShowCreate(true); setCreateForm(INIT_FORM); setCreateError(''); }}>
          + Benutzer anlegen
        </button>
      </div>

      <div className="card">
        {error && <div className="alert alert-error">{error}</div>}

        {loading ? (
          <div style={{ textAlign: 'center', padding: '2rem' }}><div className="loading-spinner" style={{ margin: '0 auto' }} /></div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr><th>Name</th><th>E-Mail</th><th>Rolle</th><th>Status</th><th>Letzter Login</th><th>Seit</th></tr>
              </thead>
              <tbody>
                {rows.map(r => (
                  <tr key={r.id} onClick={() => openEdit(r)}>
                    <td style={{ fontWeight: 500 }}>{r.vorname} {r.nachname}</td>
                    <td className="td-muted">{r.email}</td>
                    <td>{ROLLEN_LABELS[r.rolle] || r.rolle}</td>
                    <td>
                      <span style={{ padding: '2px 8px', borderRadius: 10, fontSize: '0.78rem', fontWeight: 600,
                        background: r.aktiv ? '#e8f5e9' : '#f5f5f5', color: r.aktiv ? '#2e7d32' : '#616161' }}>
                        {r.aktiv ? 'Aktiv' : 'Deaktiviert'}
                      </span>
                    </td>
                    <td className="td-muted">{fmtDate(r.letzter_login)}</td>
                    <td className="td-muted">{fmtDate(r.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showCreate && (
        <Modal title="Neuen Benutzer anlegen" onClose={() => setShowCreate(false)} size="sm">
          {createError && <div className="alert alert-error">{createError}</div>}
          <form onSubmit={handleCreate}>
            <div className="detail-grid">
              <div className="form-group">
                <label className="form-label">Vorname</label>
                <input className="form-control" value={createForm.vorname} onChange={e => setCreateForm(p => ({...p, vorname: e.target.value}))} />
              </div>
              <div className="form-group">
                <label className="form-label">Nachname</label>
                <input className="form-control" value={createForm.nachname} onChange={e => setCreateForm(p => ({...p, nachname: e.target.value}))} />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">E-Mail</label>
              <input type="email" className="form-control" value={createForm.email} onChange={e => setCreateForm(p => ({...p, email: e.target.value}))} />
            </div>
            <div className="form-group">
              <label className="form-label">Rolle</label>
              <select className="form-control" value={createForm.rolle} onChange={e => setCreateForm(p => ({...p, rolle: e.target.value}))}>
                {ROLLEN.map(r => <option key={r} value={r}>{ROLLEN_LABELS[r]}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Passwort (min. 8 Zeichen)</label>
              <input type="password" className="form-control" value={createForm.passwort} onChange={e => setCreateForm(p => ({...p, passwort: e.target.value}))} />
            </div>
            <div className="modal-actions">
              <button type="button" className="btn btn-secondary btn-sm" onClick={() => setShowCreate(false)}>Abbrechen</button>
              <button type="submit" className="btn btn-primary btn-sm" disabled={creating}>{creating ? 'Anlegen…' : 'Anlegen'}</button>
            </div>
          </form>
        </Modal>
      )}

      {selected && (
        <Modal title={`Benutzer: ${selected.vorname} ${selected.nachname}`} onClose={() => setSelected(null)} size="sm">
          {editError && <div className="alert alert-error">{editError}</div>}
          <div className="detail-grid">
            <div className="form-group">
              <label className="form-label">Vorname</label>
              <input className="form-control" value={editForm.vorname} onChange={e => setEditForm(p => ({...p, vorname: e.target.value}))} />
            </div>
            <div className="form-group">
              <label className="form-label">Nachname</label>
              <input className="form-control" value={editForm.nachname} onChange={e => setEditForm(p => ({...p, nachname: e.target.value}))} />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Rolle</label>
            <select className="form-control" value={editForm.rolle} onChange={e => setEditForm(p => ({...p, rolle: e.target.value}))}>
              {ROLLEN.map(r => <option key={r} value={r}>{ROLLEN_LABELS[r]}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Neues Passwort <span style={{ fontWeight: 400, textTransform: 'none' }}>(leer lassen = unverändert)</span></label>
            <input type="password" className="form-control" value={editForm.passwort} onChange={e => setEditForm(p => ({...p, passwort: e.target.value}))} />
          </div>
          <div className="modal-actions">
            {selected.aktiv && (() => {
              const darfNicht = aktuellerBenutzer?.rolle === 'schwimmadmin' && selected.rolle === 'admin';
              return (
                <button className="btn btn-danger btn-sm" onClick={handleDeactivate}
                  disabled={saving || darfNicht}
                  title={darfNicht ? 'Schwimmadmins dürfen keine Administratorkonten deaktivieren' : undefined}>
                  {deactivateConfirm ? 'Wirklich deaktivieren?' : 'Deaktivieren'}
                </button>
              );
            })()}
            {deactivateConfirm && <button className="btn btn-secondary btn-sm" onClick={() => setDeactivateConfirm(false)}>Abbrechen</button>}
            <button className="btn btn-secondary btn-sm" onClick={() => setSelected(null)}>Schließen</button>
            <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>{saving ? 'Speichern…' : 'Speichern'}</button>
          </div>
        </Modal>
      )}
    </div>
  );
}
